import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, Link2, Upload, History, Share2, DollarSign } from "lucide-react";
import StaffShell from "../components/StaffShell";
import { api } from "../lib/api";

const STATUSES = ["checked_in", "diagnosing", "awaiting_approval", "awaiting_parts", "in_progress", "ready_for_pickup", "closed", "cancelled"];
const money = (c) => `$${(c / 100).toFixed(2)}`;

export default function TicketDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [edit, setEdit] = useState(null);
  const [newFinding, setNewFinding] = useState({ title: "", description: "" });
  const [estItems, setEstItems] = useState([{ description: "", qty: 1, rate: "", taxable: true }]);
  const [inv, setInv] = useState(null);
  const [vinHist, setVinHist] = useState(null);
  const [custLink, setCustLink] = useState("");

  const load = () => api(`/staff/tickets/${id}`).then((d) => { setT(d); setEdit({ status: d.status, assignment: d.assignment || "", priority: d.priority, staff_notes: d.staff_notes || "", vin: d.vehicle?.vin || "", repair_record: d.repair_record }); setInv(d.invoice ? { lines: d.invoice.lines.map((l) => ({ ...l, rate: (l.rate_cents / 100).toString() })), tax_rate_percent: d.invoice.tax_rate_percent } : { lines: [{ kind: "parts", description: "", qty: 1, rate: "", taxable: true }], tax_rate_percent: 0 }); }).catch((e) => setError(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveTicket = async () => {
    setError("");
    try {
      await api(`/staff/tickets/${id}`, { method: "PATCH", body: { version: t.version, ...edit } });
      setFlash("Ticket updated.");
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const uploadMedia = async (findingId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("finding_id", findingId);
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/staff/tickets/${id}/media?finding_id=${findingId}`, {
      method: "POST", body: fd, credentials: "include",
    });
    if (!res.ok) { setError((await res.json()).detail || "Upload failed"); return; }
    load();
  };

  const addFinding = async (e) => {
    e.preventDefault();
    await api(`/staff/tickets/${id}/findings`, { method: "POST", body: newFinding });
    setNewFinding({ title: "", description: "" });
    load();
  };

  const issueLink = async () => {
    const res = await api(`/staff/tickets/${id}/customer-link`, { method: "POST" });
    setCustLink(`${window.location.origin}/customer#t=${res.token}`);
    setFlash("New customer link issued — copy it now; it is shown once.");
    load();
  };

  const lookupVin = async () => {
    const v = edit.vin.trim();
    if (v.length !== 17) { setError("VIN must be 17 characters"); return; }
    const d = await api(`/staff/vin-history?vin=${encodeURIComponent(v)}`);
    setVinHist(d.tickets);
  };

  const createEstimate = async () => {
    setError("");
    try {
      await api(`/staff/tickets/${id}/estimates`, { method: "POST", body: {
        items: estItems.filter((i) => i.description).map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 0, rate_cents: Math.round((parseFloat(i.rate) || 0) * 100), taxable: !!i.taxable })),
      } });
      setFlash("Estimate revision sent to the customer view.");
      setEstItems([{ description: "", qty: 1, rate: "", taxable: true }]);
      load();
    } catch (e) { setError(e.message); }
  };

  const saveInvoice = async () => {
    setError("");
    try {
      await api(`/staff/tickets/${id}/invoice`, { method: "POST", body: {
        tax_rate_percent: parseFloat(inv.tax_rate_percent) || 0,
        lines: inv.lines.filter((l) => l.description).map((l) => ({ kind: l.kind, description: l.description, qty: parseFloat(l.qty) || 0, rate_cents: Math.round((parseFloat(l.rate) || 0) * 100), taxable: !!l.taxable })),
      } });
      setFlash("Invoice saved.");
      load();
    } catch (e) { setError(e.message); }
  };

  const markPaid = async () => {
    const ref = window.prompt("Register receipt reference (optional):") || "";
    await api(`/staff/invoices/${t.invoice.id}/mark-paid`, { method: "POST", body: { receipt_reference: ref } });
    load();
  };

  if (!t) return <StaffShell title="Ticket"><div className="skeleton" style={{ height: 300 }} /></StaffShell>;

  const invTotals = (() => {
    if (!inv) return null;
    const lines = inv.lines.filter((l) => l.description);
    const subtotal = lines.reduce((s, l) => s + Math.round((parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0) * 100), 0);
    const taxable = lines.reduce((s, l) => s + (l.taxable ? Math.round((parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0) * 100) : 0), 0);
    const tax = Math.round(taxable * (parseFloat(inv.tax_rate_percent) || 0) / 100);
    return { subtotal, tax, total: subtotal + tax };
  })();

  return (
    <StaffShell title={<span className="mono">{t.number}</span>}>
      {error && <div className="error-box" role="alert" data-testid="ticket-error">{error}</div>}
      {flash && <div className="ok-box" data-testid="ticket-flash">{flash}</div>}

      <div className="card" style={{ padding: 24, marginBottom: 18 }} data-testid="ticket-summary">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <span className={`pill ${t.status}`} data-testid="ticket-status-pill">{t.status.replace(/_/g, " ")}</span>
          <span className={`pill ${t.payment?.paid ? "paid" : ""}`}>{t.payment?.paid ? `paid ${t.payment.paid_at?.slice(0, 10)}` : "unpaid"}</span>
          {t.request_id && <span className="pill">from {t.request_id}</span>}
          <span className="muted mono" style={{ fontSize: 12 }}>v{t.version}</span>
        </div>
        <div className="grid-2" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            <strong>{t.customer.first_name} {t.customer.last_name}</strong><br />
            <a href={`tel:${t.customer.phone}`} style={{ color: "var(--blue-bright)" }}>{t.customer.phone}</a> · {t.customer.email}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.8 }}>
            {t.vehicle.year} {t.vehicle.make} {t.vehicle.model}{t.vehicle.vin ? <> · <span className="mono">{t.vehicle.vin}</span></> : null}{t.vehicle.mileage ? ` · ${t.vehicle.mileage} mi` : ""}<br />
            <span className="pill">{t.service}</span>
          </div>
        </div>
        <p className="muted" style={{ margin: "12px 0 0", fontSize: 14 }}>Concern: {t.concern}</p>
      </div>

      <div className="grid-2">
        <div className="card" style={{ padding: 24 }} data-testid="ticket-edit-panel">
          <h3 style={{ fontSize: 17, marginBottom: 14 }}>Status &amp; assignment</h3>
          <div className="field-pair">
            <div className="field"><label>Status</label>
              <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="ticket-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="field"><label>Priority</label>
              <select value={edit.priority} onChange={(e) => setEdit({ ...edit, priority: e.target.value })}>
                {["normal", "high", "low"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="field"><label>Assigned to</label><input value={edit.assignment} onChange={(e) => setEdit({ ...edit, assignment: e.target.value })} /></div>
          <div className="field"><label>Private scheduling notes (never shown to customer)</label><textarea rows={2} value={edit.staff_notes} onChange={(e) => setEdit({ ...edit, staff_notes: e.target.value })} data-testid="ticket-notes-input" /></div>
          <div className="field"><label>VIN (normalized, enables vehicle history)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input value={edit.vin} maxLength={17} onChange={(e) => setEdit({ ...edit, vin: e.target.value.toUpperCase() })} data-testid="ticket-vin-input" />
              <button type="button" className="btn btn-ghost btn-sm" onClick={lookupVin} data-testid="ticket-vin-history-button"><History size={14} /> History</button>
            </div>
          </div>
          <button className="btn btn-blue btn-sm" onClick={saveTicket} data-testid="ticket-save-button">Save ticket</button>
        </div>

        <div className="card" style={{ padding: 24 }} data-testid="ticket-repair-record">
          <h3 style={{ fontSize: 17, marginBottom: 14 }}>Repair record (private)</h3>
          {Object.entries({ findings: "Findings", confirmed_cause: "Confirmed cause", work: "Work performed", parts: "Parts used", final_tests: "Final tests", outcome: "Outcome" }).map(([k, label]) => (
            <div className="field" key={k}><label>{label}</label>
              <textarea rows={2} value={edit.repair_record[k] || ""} onChange={(e) => setEdit({ ...edit, repair_record: { ...edit.repair_record, [k]: e.target.value } })} data-testid={`ticket-rr-${k}-input`} />
            </div>
          ))}
          <button className="btn btn-blue btn-sm" onClick={saveTicket} data-testid="ticket-save-rr-button">Save repair record</button>
        </div>
      </div>

      {vinHist && (
        <div className="card" style={{ padding: 24, marginTop: 18 }} data-testid="vin-history-panel">
          <h3 style={{ fontSize: 17, marginBottom: 12 }}>Vehicle history for {edit.vin}</h3>
          {vinHist.length === 0 && <p className="muted" style={{ fontSize: 14 }}>No prior visits under this VIN.</p>}
          <table className="data">{vinHist.map((h) => (
            <tr key={h.id}><td><strong className="mono">{h.number}</strong></td><td>{(h.created_at || "").slice(0, 10)}</td><td>{h.service}</td><td><span className={`pill ${h.status}`}>{h.status.replace(/_/g, " ")}</span></td><td className="muted" style={{ maxWidth: 300 }}>{h.concern}</td></tr>
          ))}</table>
        </div>
      )}

      <div className="card" style={{ padding: 24, marginTop: 18 }} data-testid="findings-panel">
        <h3 style={{ fontSize: 17, marginBottom: 14 }}>Inspection findings &amp; media</h3>
        {t.findings.map((f) => (
          <div key={f.id} style={{ borderTop: "1px solid var(--line)", padding: "14px 0" }} data-testid={`finding-${f.id}`}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ fontFamily: "var(--font-display)" }}>{f.title}</strong>
              <span className={`pill ${f.shared ? "published" : ""}`}>{f.shared ? "shared with customer" : "staff draft"}</span>
              <button className="btn btn-ghost btn-sm" onClick={async () => { await api(`/staff/findings/${f.id}`, { method: "PATCH", body: { shared: !f.shared } }); load(); }} data-testid={`finding-share-${f.id}`}>
                <Share2 size={13} /> {f.shared ? "Unshare" : "Share"}
              </button>
              <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                <Upload size={13} /> Add photo/video
                <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4" hidden onChange={(e) => e.target.files[0] && uploadMedia(f.id, e.target.files[0])} />
              </label>
            </div>
            <p className="muted" style={{ fontSize: 13.5, margin: "8px 0" }}>{f.description}</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {f.media.map((m) => m.content_type === "video/mp4" ? (
                <video key={m.id} src={`/api/media/${m.id}`} controls style={{ maxWidth: 220, borderRadius: 10 }} data-testid={`finding-media-${m.id}`} />
              ) : (
                <img key={m.id} src={`/api/media/${m.id}`} alt={m.filename} style={{ maxWidth: 220, borderRadius: 10 }} data-testid={`finding-media-${m.id}`} />
              ))}
            </div>
          </div>
        ))}
        <form onSubmit={addFinding} style={{ borderTop: "1px solid var(--line)", paddingTop: 16, marginTop: 8 }}>
          <div className="field-pair">
            <div className="field"><label>New finding title</label><input required value={newFinding.title} onChange={(e) => setNewFinding({ ...newFinding, title: e.target.value })} data-testid="finding-title-input" /></div>
            <div className="field"><label>Description</label><input required value={newFinding.description} onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })} data-testid="finding-desc-input" /></div>
          </div>
          <button className="btn btn-blue btn-sm" data-testid="finding-add-button">Add finding</button>
        </form>
      </div>

      <div className="card" style={{ padding: 24, marginTop: 18 }} data-testid="estimates-panel">
        <h3 style={{ fontSize: 17, marginBottom: 6 }}>Estimates (immutable revisions)</h3>
        <p className="form-note" style={{ marginBottom: 14 }}>Each save creates the next revision. Customers approve or decline every item; responses are audited.</p>
        {t.estimates.map((e) => (
          <div key={e.id} style={{ borderTop: "1px solid var(--line)", padding: "12px 0" }} data-testid={`estimate-${e.id}`}>
            <strong>Revision {e.revision}</strong> <span className={`pill ${e.status}`}>{e.status}</span>
            <table className="data" style={{ marginTop: 8 }}>
              <tbody>
                {e.items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.description}</td>
                    <td className="mono">{it.qty} × {money(it.rate_cents)}</td>
                    <td><span className={`pill ${it.decision === "approved" ? "paid" : it.decision === "declined" ? "cancelled" : ""}`}>{it.decision}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {e.status === "sent" && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={async () => { await api("/staff/estimates/withdraw", { method: "POST", body: { estimate_id: e.id } }); load(); }}>Withdraw</button>
            )}
            {e.response && <p className="form-note" style={{ marginTop: 8 }}>Customer {e.response.confirm_name} responded {e.response.responded_at?.slice(0, 16).replace("T", " ")} — review before performing work.</p>}
          </div>
        ))}
        <div style={{ borderTop: "1px solid var(--line)", paddingTop: 16, marginTop: 8 }}>
          {estItems.map((it, i) => (
            <div className="field-pair" key={i} style={{ marginBottom: 8 }}>
              <input placeholder="Work or part description" value={it.description} onChange={(e) => setEstItems(estItems.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} data-testid={`estimate-item-desc-${i}`} />
              <div style={{ display: "flex", gap: 8 }}>
                <input placeholder="Qty" type="number" step="0.1" value={it.qty} onChange={(e) => setEstItems(estItems.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} style={{ width: 80 }} />
                <input placeholder="Rate $" type="number" step="0.01" value={it.rate} onChange={(e) => setEstItems(estItems.map((x, j) => j === i ? { ...x, rate: e.target.value } : x))} style={{ width: 110 }} />
                <label className="consent" style={{ margin: 0, whiteSpace: "nowrap" }}><input type="checkbox" checked={it.taxable} onChange={(e) => setEstItems(estItems.map((x, j) => j === i ? { ...x, taxable: e.target.checked } : x))} /> tax</label>
              </div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEstItems([...estItems, { description: "", qty: 1, rate: "", taxable: true }])}>+ line</button>
            <button type="button" className="btn btn-blue btn-sm" onClick={createEstimate} data-testid="estimate-create-button">Send new revision</button>
          </div>
        </div>
      </div>

      <div className="card no-print" style={{ padding: 24, marginTop: 18 }} data-testid="customer-link-panel">
        <h3 style={{ fontSize: 17, marginBottom: 8 }}>Customer access link</h3>
        <p className="form-note" style={{ marginBottom: 12 }}>
          Random single-ticket link, expires in 14 days. Issuing a new link revokes the old one. The token is shown once.
        </p>
        <button className="btn btn-blue btn-sm" onClick={issueLink} data-testid="customer-link-issue"><Link2 size={14} /> Issue new link</button>
        {custLink && <p className="mono ok-box" style={{ wordBreak: "break-all" }} data-testid="customer-link-value">{custLink}</p>}
        {t.customer_link && !custLink && <p className="form-note">An active link exists (issued {(t.customer_link.expires_at || "").slice(0, 10)}). Issue a new one to replace it.</p>}
      </div>

      {inv && invTotals && (
        <div style={{ marginTop: 18 }} data-testid="invoice-panel">
          <div className="card no-print" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 17, marginBottom: 14 }}><DollarSign size={16} style={{ verticalAlign: -3 }} /> Parts &amp; labor invoice</h3>
            {inv.lines.concat(inv.lines.length === 0 ? [{}] : []).map((l, i) => i < inv.lines.length || l === undefined ? null : null)}
            {inv.lines.map((l, i) => (
              <div className="field-pair" key={i} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <select value={l.kind} onChange={(e) => setInv({ ...inv, lines: inv.lines.map((x, j) => j === i ? { ...x, kind: e.target.value } : x) })} style={{ width: 110 }}>
                    <option value="parts">parts</option><option value="labor">labor</option>
                  </select>
                  <input placeholder="Description" value={l.description} onChange={(e) => setInv({ ...inv, lines: inv.lines.map((x, j) => j === i ? { ...x, description: e.target.value } : x) })} style={{ flex: 1 }} data-testid={`invoice-desc-${i}`} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input placeholder="Qty/hours" type="number" step="0.1" value={l.qty} onChange={(e) => setInv({ ...inv, lines: inv.lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) })} style={{ width: 90 }} />
                  <input placeholder="Rate $" type="number" step="0.01" value={l.rate} onChange={(e) => setInv({ ...inv, lines: inv.lines.map((x, j) => j === i ? { ...x, rate: e.target.value } : x) })} style={{ width: 110 }} data-testid={`invoice-rate-${i}`} />
                  <label className="consent" style={{ margin: 0, whiteSpace: "nowrap" }}><input type="checkbox" checked={l.taxable} onChange={(e) => setInv({ ...inv, lines: inv.lines.map((x, j) => j === i ? { ...x, taxable: e.target.checked } : x) })} /> tax</label>
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setInv({ ...inv, lines: [...inv.lines, { kind: "parts", description: "", qty: 1, rate: "", taxable: true }] })}>+ line</button>
              <label className="muted" style={{ fontSize: 13 }}>Tax rate %<input type="number" step="0.01" value={inv.tax_rate_percent} onChange={(e) => setInv({ ...inv, tax_rate_percent: e.target.value })} style={{ width: 90, marginLeft: 8, background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", padding: "6px 10px" }} data-testid="invoice-tax-input" /></label>
              <button type="button" className="btn btn-blue btn-sm" onClick={saveInvoice} data-testid="invoice-save-button">Save invoice</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => window.print()} data-testid="invoice-print-button"><Printer size={14} /> Print / PDF</button>
              {!t.payment?.paid && t.invoice && (
                <button type="button" className="btn btn-red btn-sm" onClick={markPaid} data-testid="invoice-mark-paid"><DollarSign size={14} /> Mark Paid at register</button>
              )}
            </div>
            <p className="mono muted" style={{ marginTop: 12, fontSize: 13 }}>
              Subtotal {money(invTotals.subtotal)} · Tax {money(invTotals.tax)} · <strong style={{ color: "var(--ink)" }}>Total {money(invTotals.total)}</strong>
            </p>
          </div>

          {t.invoice && (
            <div className="print-sheet" style={{ marginTop: 18 }} data-testid="invoice-print-sheet">
              <div className="inv-head">
                <div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 24 }}><span style={{ color: "#D5272E" }}>C.C.C.</span> <span style={{ color: "#145AD3" }}>Auto Electric</span></div>
                  <div style={{ fontSize: 13, lineHeight: 1.7 }}>{t.invoice.shop_address}<br />{t.invoice.shop_phone}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <img src="/assets/ccc-mechanic-mascot-portrait.png" alt="C.C.C. mascot" style={{ margin: "0 auto" }} />
                  <div className="mono" style={{ fontSize: 12, marginTop: 6 }}>Invoice {t.invoice.number}<br />Ticket {t.number}</div>
                </div>
              </div>
              <p style={{ fontSize: 14 }}>
                <strong>{t.customer.first_name} {t.customer.last_name}</strong><br />
                {t.vehicle.year} {t.vehicle.make} {t.vehicle.model}{t.vehicle.vin ? ` · ${t.vehicle.vin}` : ""}
              </p>
              <table>
                <thead><tr><th>Description</th><th>Kind</th><th>Qty/hrs</th><th>Rate</th><th>Amount</th><th>Tax</th></tr></thead>
                <tbody>
                  {t.invoice.lines.map((l) => (
                    <tr key={l.id}><td>{l.description}</td><td>{l.kind}</td><td>{l.qty}</td><td>{money(l.rate_cents)}</td>
                      <td>{money(Math.round(l.qty * l.rate_cents))}</td><td>{l.taxable ? "yes" : "—"}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="totals">
                <div><span>Subtotal</span><strong>{money(t.invoice.totals.subtotal_cents)}</strong></div>
                <div><span>Tax ({t.invoice.tax_rate_percent}%)</span><strong>{money(t.invoice.totals.tax_cents)}</strong></div>
                <div className="grand"><span>Total</span><strong>{money(t.invoice.totals.total_cents)}</strong></div>
                <div style={{ fontSize: 12, color: "#52627b" }}><span>Payment</span><span>{t.invoice.payment?.paid ? `PAID ${(t.invoice.payment.paid_at || "").slice(0, 10)}` : "Due at register"}</span></div>
              </div>
              <p style={{ fontSize: 11.5, color: "#52627b", marginTop: 20 }}>Payment is handled at our in-store register. This invoice is a sales record, not a payment authorization. Thank you for trusting C.C.C. Auto Electric.</p>
            </div>
          )}
        </div>
      )}
    </StaffShell>
  );
}
